import DialogBase from "@components/ui/dialog-base";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";

import {
  CircleStencil,
  Cropper,
  ImageRestriction,
  Priority,
  RectangleStencil,
  type CropperRef,
  type CropperState,
  type Size,
} from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

import type { IMediaFile } from "@/shared/types";
import Button from "@components/ui/button";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useCropperDialogSubmit, useCropperEdgePan } from "../hooks";
import type { CropData, CropShape } from "../types";
import { CropperDialogActions } from "./cropper-dialog-actions";

const DIALOG_ID = "cropper-dialog";

const getDefaultSize = (state: CropperState): Size => {
  return {
    width: state.imageSize.width,
    height: state.imageSize.height,
  };
};

interface IProps {
  imageSrc: string;
  /** When provided, this URL is shown in the cropper so the user always sees the original image with crop boundaries. */
  originalImageSrc?: string;
  /** When provided, the cropper opens with this crop area and transforms so the user sees the original image (but cropped with the boundaries). */
  defaultCropData?: CropData;
  /** When the editor image shape is blob or parallelogram, the cropper uses square stencil. */
  editorShape?: string;
  onCrop?: (dataUrl: string, canvas: HTMLCanvasElement | null) => void;
  onCropAndUpload?: (
    mediaFile: IMediaFile,
    canvas: HTMLCanvasElement | null,
  ) => void;
  /** Called when the user saves a crop, so the caller can persist crop data and re-open with the same boundaries. */
  onCropDataSave?: (data: CropData) => void;
  /** When provided, the dialog closes immediately and this callback receives
   *  the blob for background upload by the caller (which stays mounted). */
  onBackgroundUpload?: (blob: Blob, blobUrl: string) => void;
}

const getInitialCropShape = (
  defaultShape: CropShape | undefined,
  editorShape: string | undefined,
): CropShape => {
  if (editorShape === "blob" || editorShape === "parallelogram") {
    return "square";
  }
  return defaultShape ?? "free";
};

export const CropperDialog = ({
  imageSrc,
  originalImageSrc,
  defaultCropData,
  editorShape,
  onCrop,
  onCropAndUpload,
  onCropDataSave,
  onBackgroundUpload,
}: IProps) => {
  const [isIniting, setIsIniting] = useState(true);
  const [showCropper, setShowCropper] = useState(false);
  const [cropShape, setCropShape] = useState<CropShape>(() =>
    getInitialCropShape(defaultCropData?.shape, editorShape),
  );
  const dispatch = useAppDispatch();
  const cropperRef = useRef<CropperRef>(null);
  // Auto-pan the image when the crop selection is dragged to a boundary edge,
  // so it can travel across the whole image even when zoomed in.
  const edgePanRef = useCropperEdgePan(cropperRef);
  const [hasChanges, setHasChanges] = useState(false);
  const [cropperKey, setCropperKey] = useState(0);

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleCropperUpdate = useCallback(() => {
    if (!isIniting) setHasChanges(true);
  }, [isIniting]);

  const handleReset = useCallback(() => {
    // Remount the cropper to restore the initial state
    setCropperKey((k) => k + 1);
    setCropShape(getInitialCropShape(defaultCropData?.shape, editorShape));
    setHasChanges(false);
    setIsIniting(true);
  }, [defaultCropData, editorShape]);

  const { isSaving, save } = useCropperDialogSubmit({
    onClose: handleClose,
    imageSrc,
    cropperRef,
    onCropDataSave,
    cropShape,
    ...(onCrop && { onCrop }),
    ...(onCropAndUpload && { onCropAndUpload }),
    ...(onBackgroundUpload && { onBackgroundUpload }),
  });
  const [processedImageSrc, setProcessedImageSrc] = useState<string>("");

  const imageUrl = originalImageSrc ?? imageSrc;
  const url = useMemo(() => {
    return imageUrl.split("?")[0];
  }, [imageUrl]);

  // Pre-process SVG images to load at higher resolution
  useEffect(() => {
    const loadImage = async () => {
      // Check if it's an SVG by trying to fetch and check content-type
      try {
        const response = await fetch(url, { method: "HEAD" });
        const contentType = response.headers.get("content-type");

        if (contentType?.includes("svg")) {
          // Load SVG at high resolution by converting to canvas
          const img = new Image();
          img.crossOrigin = "anonymous";

          img.onload = () => {
            // Preserve aspect ratio: scale so the longer side is 1920px (no stretching)
            const maxSide = 1920;
            let w = img.naturalWidth || img.width || 1;
            let h = img.naturalHeight || img.height || 1;
            const scale = maxSide / Math.max(w, h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);

            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;

            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = "high";
              ctx.drawImage(img, 0, 0, w, h);

              setProcessedImageSrc(canvas.toDataURL("image/png"));
            } else {
              setProcessedImageSrc(url);
            }
          };

          img.onerror = () => {
            setProcessedImageSrc(url);
          };

          img.src = url;
        } else {
          setProcessedImageSrc(url);
        }
      } catch (error) {
        console.error("Error processing image:", error);
        setProcessedImageSrc(url);
      }
    };

    loadImage();
  }, [url]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCropper(true);
    }, 250);
    return () => clearTimeout(timer);
  }, []);

  return (
    // tw-w-[100dvw] (not tw-w-full) is intentional: the dialog renders in
    // a FloatingPortal whose centered child takes its natural content
    // width — `w-full` would resolve to that natural width and overflow
    // narrow viewports (chrome-extension side panel). 100dvw + a max-w
    // gives us mobile-fit on narrow and the original 650px on desktop.
    <DialogBase className="cropper-dialog tw-w-[100dvw] tw-max-w-[650px] tw-flex tw-flex-col tw-max-h-[100svh]">
      {/* Header — title hides under ~420px so the action buttons always
          fit; on narrow chrome-extension panels (~360px) we keep Reset
          / Go back / Save visible and let the dialog title go off-screen. */}
      <header className="cropper-dialog__header tw-relative tw-z-10 tw-flex tw-h-16 tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-rounded-t-lg tw-border-b tw-border-solid tw-border-[rgb(18_3_62/0.08)] tw-bg-white tw-px-3 tw-py-0 sm:tw-gap-4 sm:tw-px-6">
        <h2 className="tw-m-0 tw-shrink tw-truncate tw-font-sans tw-text-base tw-font-semibold tw-leading-5 tw-text-[#12033E] tw-antialiased tw-hidden sm:tw-block">
          Crop image
        </h2>
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-end tw-gap-1.5 sm:tw-gap-[8px]">
          {hasChanges && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={isSaving}
          >
            Go back
          </Button>
          <Button
            size="sm"
            onClick={() => save()}
            isLoading={isSaving || isIniting || !showCropper}
          >
            {isSaving ? (
              <>
                <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </header>
      {/* Content — width shrinks to fit; height caps at the smaller of
          500px and 70svh so the toolbar at the bottom plus header at the
          top still fit on short viewports (chrome extension side panel). */}
      <DialogBase.Content className="tw-relative tw-z-0 tw-h-[min(500px,70svh)] tw-w-full tw-overflow-hidden !tw-px-0 tw-pt-6 tw-pb-20 sm:tw-pt-10">
        {showCropper && processedImageSrc && (
          <>
            <div
              ref={edgePanRef}
              className={cn(
                "tw-mx-auto tw-h-full tw-max-w-[95%] tw-w-full",
                isSaving && "tw-pointer-events-none tw-opacity-60",
              )}
            >
              <Cropper
                ref={cropperRef}
                key={cropperKey}
                className="tw-h-full tw-w-full"
                onReady={() => setIsIniting(false)}
                onUpdate={handleCropperUpdate}
                src={processedImageSrc}
                stencilComponent={
                  cropShape === "circle" ? CircleStencil : RectangleStencil
                }
                stencilProps={{
                  grid: cropShape !== "circle",
                }}
                aspectRatio={
                  cropShape === "square"
                    ? { minimum: 1, maximum: 1 }
                    : undefined
                }
                priority={Priority.visibleArea}
                imageRestriction={ImageRestriction.none}
                defaultSize={defaultCropData ? undefined : getDefaultSize}
                defaultCoordinates={defaultCropData?.coordinates}
                defaultTransforms={defaultCropData?.transforms}
              />
            </div>
            <div
              className={cn(
                // max-w + overflow-x-auto so even if labels reappear or
                // more actions are added the toolbar never bleeds past
                // the panel edge.
                "cropper-dialog__toolbar tw-absolute tw-bottom-3 tw-left-1/2 tw-z-10 tw-flex tw-h-[46px] tw-max-w-[calc(100%-1rem)] tw-w-fit tw-flex-row tw-items-center tw-gap-0.5 tw-rounded-lg tw-bg-white tw-px-1 tw-py-0 -tw-translate-x-1/2 tw-overflow-x-auto sm:tw-bottom-6 sm:tw-gap-1 sm:tw-px-2",
                isSaving && "tw-pointer-events-none tw-opacity-60",
              )}
            >
              <CropperDialogActions
                cropperRef={cropperRef as RefObject<CropperRef>}
                cropShape={cropShape}
                onCropShapeChange={setCropShape}
              />
            </div>
          </>
        )}
        {isIniting && (
          <div className="tw-absolute tw-left-0 tw-top-0 tw-z-50 tw-flex tw-size-full tw-items-center tw-justify-center">
            <Spinner className="tw-size-8 tw-border-4 tw-border-white" />
          </div>
        )}
      </DialogBase.Content>
    </DialogBase>
  );
};

CropperDialog.dialogId = DIALOG_ID;
